/**
 * Ob eine hochgeladene Datei BPMN ist.
 *
 * Der Editor prüft an seinen bestehenden Einstiegen mit `startsWith('<?xml')` — das genügt dort,
 * weil das XML aus der eigenen Ablage kommt. Eine Datei, die jemand von der Festplatte zieht, ist
 * eine andere Sache: Sie kann alles sein, und `importXML` beantwortet die Frage erst, nachdem es
 * das Diagramm im Editor ersetzt hat. Ein Fehlschlag danach ist teuer — die vorige Zeichnung ist
 * dann weg.
 *
 * Geprüft wird deshalb vorher, und an zwei Merkmalen: das Wurzelelement heißt `definitions`, und
 * irgendwo steht der BPMN-Namensraum. Das erste allein ließe jedes XML durch, das zweite allein
 * jede Datei, die den Namensraum bloß erwähnt.
 */

/** Der Namensraum, den jede BPMN-Datei führt. */
const BPMN_NAMESPACE = 'http://www.omg.org/spec/BPMN/20100524/MODEL';

/**
 * Ab hier wird nicht mehr gelesen.
 *
 * Ein BPMN-Diagramm ist Text; fünf Megabyte davon wären ein Dokument, das kein Mensch gezeichnet
 * hat. Die Grenze schützt nicht vor Angriff, sondern vor dem Versehen — der Datei, die jemand
 * verwechselt hat.
 */
export const MAX_BPMN_BYTES = 5 * 1024 * 1024;

/**
 * Der Vorspann, den XML vor dem Wurzelelement haben darf.
 *
 * Byte-Reihenfolge-Marke, Deklaration, Kommentare, Verarbeitungsanweisungen, Dokumenttyp — alles
 * erlaubt und alles kein Wurzelelement. Wer nur `startsWith` prüft, weist eine gültige Datei ab,
 * weil sie mit einem Kommentar beginnt.
 */
const stripProlog = (text: string): string => {
    let rest = text.replace(/^﻿/, '').trimStart();

    for (;;) {
        const before = rest;

        rest = rest
            .replace(/^<\?[\s\S]*?\?>/, '')
            .replace(/^<!--[\s\S]*?-->/, '')
            .replace(/^<!DOCTYPE[^>]*>/i, '')
            .trimStart();

        if (rest === before) return rest;
    }
};

/** Der Name des Wurzelelements, ohne Namensraumkürzel — oder `null`, wo keines zu finden ist. */
export const rootElementOf = (text: unknown): string | null => {
    if (typeof text !== 'string') return null;

    const match = /^<([A-Za-z_][\w.-]*:)?([A-Za-z_][\w.-]*)/.exec(stripProlog(text));

    return match ? match[2] : null;
};

export type BpmnRejection = 'leer' | 'zuGross' | 'keinXml' | 'keinBpmn';

/**
 * Warum eine Datei nicht angenommen wird — oder `null`, wenn sie es wird.
 *
 * Ein Grund statt eines Wahrheitswerts, weil die Meldung an den Leser davon abhängt: „zu groß" und
 * „kein BPMN" führen zu verschiedenen nächsten Schritten.
 */
export const bpmnRejection = (text: unknown, bytes?: number): BpmnRejection | null => {
    if (typeof text !== 'string' || !text.trim()) return 'leer';
    if (typeof bytes === 'number' && bytes > MAX_BPMN_BYTES) return 'zuGross';

    const root = rootElementOf(text);
    if (!root) return 'keinXml';
    if (root !== 'definitions') return 'keinBpmn';

    // Der Namensraum als zweites Merkmal: `definitions` heißt auch das Wurzelelement mancher
    // anderer Modellsprache, und ein DMN- oder CMMN-Diagramm im BPMN-Editor ist kein Zufall wert.
    return text.includes(BPMN_NAMESPACE) ? null : 'keinBpmn';
};

/** Kurzform für die Stelle, an der nur die Antwort zählt. */
export const looksLikeBpmn = (text: unknown, bytes?: number): boolean =>
    bpmnRejection(text, bytes) === null;

/** Was der Import vom Editor braucht — mehr als das ist hier nicht von Belang. */
export interface BpmnTarget {
    importXML: (xml: string) => Promise<unknown>;
    saveXML: (options: { format: boolean }) => Promise<{ xml?: string }>;
    get: (name: string) => any;
}

export interface ImportOutcome {
    /** Der Grund der Ablehnung — oder `null`, wenn importiert wurde. */
    rejection: BpmnRejection | null;
    /** Wahr, wenn `importXML` selbst gescheitert ist. */
    failed?: boolean;
}

/**
 * Eine geprüfte Datei in den Editor holen und die Änderung melden.
 *
 * Zwei Dinge stehen hier zusammen, die auseinanderzuhalten teuer wäre:
 *
 * Erstens wird **vor** dem Import geprüft. `importXML` beantwortet die Frage, ob die Datei BPMN
 * ist, erst nachdem es das Diagramm ersetzt hat; eine Ablehnung danach kostet die Zeichnung, die
 * vorher da war. Bei Ablehnung wird der Editor deshalb nicht angefasst.
 *
 * Zweitens wird die Änderung **selbst** gemeldet. Der Lauscher des Editors, der sonst jede
 * Änderung nach oben gibt, schweigt während eines Imports — ohne diesen Aufruf stünde das
 * Hochgeladene sichtbar im Editor und nirgends gespeichert.
 */
export const importBpmnInto = async (
    target: BpmnTarget,
    text: unknown,
    bytes: number | undefined,
    onImported: (xml: string) => void,
): Promise<ImportOutcome> => {
    const rejection = bpmnRejection(text, bytes);
    if (rejection) return { rejection };

    try {
        await target.importXML(text as string);
    } catch {
        return { rejection: null, failed: true };
    }

    // Das Ergebnis passend stellen; ein hochgeladenes Diagramm kommt mit fremdem Ausschnitt.
    target.get('canvas')?.zoom('fit-viewport');

    const { xml } = await target.saveXML({ format: true });
    if (xml) onImported(xml);

    return { rejection: null };
};
